import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, Key, UserCircle, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";

const empty = { name: "", email: "", phone: "", address: "", notes: "", cc_emails: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [portalFor, setPortalFor] = useState(null);
  const [portalPw, setPortalPw] = useState("");
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, email: c.email || "", phone: c.phone || "", address: c.address || "", notes: c.notes || "", cc_emails: (c.cc_emails || []).join(", ") }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("Le nom est requis."); return; }
    const payload = { ...form, email: form.email || null, cc_emails: (form.cc_emails || "").split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean) };
    try {
      if (editId) await api.put(`/clients/${editId}`, payload);
      else await api.post("/clients", payload);
      toast.success(editId ? "Client mis à jour" : "Client ajouté");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async () => {
    try { await api.delete(`/clients/${delId}`); toast.success("Client supprimé"); setDelId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const savePortal = async () => {
    if (portalPw.length < 6) { toast.error("Mot de passe : min. 6 caractères."); return; }
    try {
      await api.post(`/clients/${portalFor.id}/portal-access`, { password: portalPw });
      toast.success("Accès portail configuré");
      setPortalFor(null); setPortalPw(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendInvite = async (c) => {
    if (!c.email) { toast.error("Ajoutez d'abord un courriel au client."); return; }
    try {
      const { data } = await api.post(`/clients/${c.id}/invite`);
      if (data.already_registered) toast.info("Ce client possède déjà un compte.");
      else if (data.email_sent) toast.success("Invitation envoyée — le client recevra un lien pour créer son compte.");
      else toast.warning("Invitation créée, mais courriel non envoyé (SMTP non configuré).");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Clients</h1>
          <p className="text-muted-foreground mt-1">Gérez vos clients et leurs accès au portail.</p>
        </div>
        <Button data-testid="new-client-btn" className="rounded-sm" onClick={openNew}>
          <Plus size={18} className="mr-1" /> Nouveau client
        </Button>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Nom</TableHead>
              <TableHead>Courriel</TableHead>
              <TableHead>Téléphone</TableHead>
              <TableHead>Portail</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clients.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Aucun client. Ajoutez votre premier client.</TableCell></TableRow>
            )}
            {clients.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <span>{c.name}</span>
                    {c.invite_status === "registered" && <span data-testid={`client-invite-status-${c.id}`} className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-emerald-50 text-emerald-700 border-emerald-200">Inscrit</span>}
                    {c.invite_status === "invited" && <span data-testid={`client-invite-status-${c.id}`} className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-amber-50 text-amber-700 border-amber-200">Invité</span>}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{c.email || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{c.phone || "—"}</TableCell>
                <TableCell>
                  {c.has_portal
                    ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle size={14} weight="fill" /> Actif</span>
                    : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button data-testid={`invite-client-${c.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Inviter le client à créer son compte" onClick={() => sendInvite(c)}>
                      <PaperPlaneTilt size={16} />
                    </Button>
                    <Button data-testid={`portal-btn-${c.id}`} variant="ghost" size="icon" className="h-8 w-8" title="Définir l'accès manuellement" onClick={() => { setPortalFor(c); setPortalPw(""); }}>
                      <Key size={16} className={c.has_portal ? "text-emerald-600" : ""} />
                    </Button>
                    <Button data-testid={`edit-client-${c.id}`} variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button data-testid={`delete-client-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive" onClick={() => setDelId(c.id)}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Modifier le client" : "Nouveau client"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Nom / Entreprise" required><Input data-testid="client-name" className="rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Courriel"><Input data-testid="client-email" type="email" className="rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Téléphone"><Input data-testid="client-phone" className="rounded-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Adresse"><Textarea data-testid="client-address" className="rounded-sm" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
            <Field label="Autres destinataires (CC) — séparés par des virgules"><Input data-testid="client-cc-emails" className="rounded-sm" placeholder="comptabilite@client.com, direction@client.com" value={form.cc_emails} onChange={(e) => setForm({ ...form, cc_emails: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-client-btn" className="rounded-sm" onClick={save}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Portal access */}
      <Dialog open={!!portalFor} onOpenChange={(v) => !v && setPortalFor(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><UserCircle size={20} /> Accès portail client</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Le client <strong>{portalFor?.name}</strong> pourra se connecter avec son courriel
              <strong> {portalFor?.email || "(aucun courriel — ajoutez-en un d'abord)"}</strong> et ce mot de passe.
            </p>
            <Field label="Mot de passe">
              <Input data-testid="portal-password" type="text" className="rounded-sm" placeholder="min. 6 caractères" value={portalPw} onChange={(e) => setPortalPw(e.target.value)} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setPortalFor(null)}>Annuler</Button>
            <Button data-testid="save-portal-btn" className="rounded-sm" onClick={savePortal} disabled={!portalFor?.email}>Configurer l'accès</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce client ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible. Les factures existantes ne seront pas supprimées.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-client" className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={del}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}{required && " *"}</Label>
      {children}
    </div>
  );
}
